import { lazy } from 'react';
import outlookBWLogo from '@/components/PlatformIcon/icons/outlook-bw.png';
import outlookLogo from '@/components/PlatformIcon/icons/outlook.png';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  EventExportPlatform,
  IntegrationType,
  PlatformTypes,
} from '@/types/gql.generated';
import type { DestinationModule } from '../addToCalendar/destinations/types';
import {
  getSortedCalendars,
  isSuitableIntegration,
  selectOutlookIntegrations,
} from './calendar.lib';
import { LAUNCH_KEY_OUTLOOK_MULTI } from './constants';
import { useOutlookExportModalLaunch } from './hooks/useOutlookExportModalLaunch';

// Modal/form bodies mount only when their flow opens, so they load lazily and
// the entry stays cheap for data-only consumers (copy, icons, status).
const OutlookExportModal = lazy(() =>
  import('./components/OutlookExportModal').then((module) => ({
    default: module.OutlookExportModal,
  }))
);

const OutlookCalendarPicker = lazy(() =>
  import('./components/OutlookCalendarPicker').then((module) => ({
    default: module.OutlookCalendarPicker,
  }))
);

const OutlookExportForm = lazy(() =>
  import('./components/OutlookExportForm').then((module) => ({
    default: module.OutlookExportForm,
  }))
);

const useOutlookIntegrationAvailable = (): boolean => {
  const { value } = useFeatureFlag('outlookIntegrationEnabled');
  return value;
};

// Flag-gated: while available it stands in for the Microsoft weblinks in every
// picker, and a persisted selection degrades back to the Outlook weblink when
// the flag turns off. (The flag reads false while it loads — callers that must
// bridge that window handle it themselves, see ExportCalendarSidebarBody.)
export const outlookIntegrationDestination: DestinationModule = {
  platform: PlatformTypes.OutlookIntegration,
  behavior: 'sync',
  name: 'Outlook',
  addLabel: 'Add to Outlook',
  description:
    'Changes to events you select land in Outlook Calendar within about 15 minutes. New events are not automatically added.',
  syncKind: 'sync',
  icon: { kind: 'image', src: outlookLogo, bwSrc: outlookBWLogo },
  isPinnable: true,
  persistedByServer: true,
  statusSource: 'integrationExports',
  useIsAvailable: useOutlookIntegrationAvailable,
  standsInFor: [PlatformTypes.OutlookLink, PlatformTypes.OfficeLink],
  fallbackPlatform: PlatformTypes.OutlookLink,
  ExportForm: OutlookExportForm,
  integration: {
    exportPlatform: EventExportPlatform.Outlook,
    adapter: {
      integrationType: IntegrationType.OutlookCalendar,
      select: selectOutlookIntegrations,
      getSortedCalendars,
      isSuitableIntegration,
    },
    launchPrefix: 'outlook-add',
    multiLaunchKey: LAUNCH_KEY_OUTLOOK_MULTI,
    ExportModal: OutlookExportModal,
    CalendarPicker: OutlookCalendarPicker,
    useLaunchResume: useOutlookExportModalLaunch,
  },
};
